import React, { useContext, useMemo } from 'react';
import { Route, Routes, useParams } from 'react-router-dom';
import { V3SelectedAppContext } from 'contextProviders/SplashScreenProviders/UserAndAccountProviders/V3AppsProvider';
import appTypeCheckers from 'contextProviders/SplashScreenProviders/UserAndAccountProviders/V3AppsProvider/appTypeCheckers';
import BonusAppProvider from 'contextProviders/BonusAppProvider';
import NavSection from 'kingpin/navigation/NavSection';
import AppNavItem from 'kingpin/navigation/MainNav/AppNavItem';

import appNavItemTypeCheckers from 'types/appNavItemTypeCheckers';
import EntityAppSideBar from './EntityAppSidebar';
import styled from 'styled-components';
import Typography from '../../../../../../kingpin/atoms/Typography';
import { V3PrivateIndicator } from '../../../../../../screens/V3/Content/V3PrivateIndicator';
import Row from '../../../../../../components/Common/Row';
import Icon from '../../../../../../kingpin/atoms/Icon';
import EditPinnedAssetsButton from '../EditPinnedAssetsButton';
import V3AppAdminsOnly from '../../../../../../components/V3AppAdminsOnly';
import useAppSideNav from '../useAppSideNav';
import { Scrollable } from '../../../../../../components/UserPicker/UserPicker';

const useSelectedNavItem = () => {
  const { selectedProductApp } = useContext(V3SelectedAppContext);
  const { itemKey } = useParams();

  const selectedNavItem = useMemo((): V3.Navigation.Item | undefined => {
    if (!selectedProductApp || !itemKey) return undefined;

    const allItems: V3.Navigation.Item[] =
      selectedProductApp.navigationSettings.sections.reduce((acc, s) => {
        return [...acc, ...s.items];
      }, [] as V3.Navigation.Item[]);

    const selectedItem = allItems.find((i) => i.key === itemKey);

    if (!selectedItem) return undefined;

    return selectedItem;
  }, [itemKey, selectedProductApp]);

  return selectedNavItem;
};

export const PRODUCT_APP_TITLE_HEIGHT = 80;

const TitleSectionDiv = styled.div`
  height: ${PRODUCT_APP_TITLE_HEIGHT}px;
  min-height: ${PRODUCT_APP_TITLE_HEIGHT}px;
  border-bottom: 1px solid var(--color-border-tertiary);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  padding: 20px 24px;
  gap: 8px;
`;

const TitleSection = ({ app }: { app: V3.ProductApp }) => {
  const { selectedProduct } = useContext(V3SelectedAppContext);

  const icon = useMemo(() => {
    if (!selectedProduct) {
      return undefined;
    }

    return selectedProduct.icon;
  }, [selectedProduct]);

  return (
    <TitleSectionDiv>
      <Row style={{ gap: 8 }} centerAlign>
        {icon && <Icon icon={icon} width={30} height={30} />}
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '4px',
          }}
        >
          <Typography.Header type={'H3'} isEllipsis>
            {app.label}
          </Typography.Header>
          {!app.isPublished && <V3PrivateIndicator />}
        </div>
      </Row>
    </TitleSectionDiv>
  );
};

const ProductAppSidebar = () => {
  const { selectedProductApp } = useContext(V3SelectedAppContext);
  const { sections, getItems, isAppAdmin } = useAppSideNav(selectedProductApp);

  const content = useMemo(() => {
    if (!selectedProductApp) {
      return <></>;
    }

    const isBottomPinned =
      selectedProductApp.navigationSettings.isBottomSectionPinned ?? false;
    const scrollableSections = isBottomPinned
      ? sections.slice(0, -1)
      : sections;
    const bottomSection =
      isBottomPinned && sections.length > 0
        ? sections[sections.length - 1]
        : undefined;

    const renderSection = (section: V3.Navigation.SideSection) => (
      <NavSection
        label={section.title.isEnabled ? section.title.label : undefined}
        hideBorderBottom={!section.isBottomBorderEnabled}
        key={section.key}
        subLabel={
          <V3AppAdminsOnly>
            <div className={'showOnMouseOver'}>
              <EditPinnedAssetsButton
                section={section}
                app={selectedProductApp}
              />
            </div>
          </V3AppAdminsOnly>
        }
      >
        {getItems(section).map((item) => (
          <AppNavItem
            app={selectedProductApp}
            item={item}
            key={item.key}
            isAppAdmin={isAppAdmin}
          />
        ))}
      </NavSection>
    );

    return (
      <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        {!selectedProductApp.isAppTitleHidden && (
          <TitleSection app={selectedProductApp} />
        )}
        <Scrollable style={{ flex: 1, maxHeight: '100%', overflowX: 'clip' }}>
          {scrollableSections.map(renderSection)}
        </Scrollable>
        {bottomSection && (
          <div style={{ borderTop: 'solid 1px var(--color-border-secondary)' }}>
            {renderSection(bottomSection)}
          </div>
        )}
      </div>
    );
  }, [getItems, isAppAdmin, sections, selectedProductApp]);

  if (!selectedProductApp) return null;

  if (appTypeCheckers.isBonusApp(selectedProductApp)) {
    return (
      <BonusAppProvider app={selectedProductApp}>{content}</BonusAppProvider>
    );
  }

  return <>{content}</>;
};

const SidebarSwitch = () => {
  const selectedNavItem = useSelectedNavItem();

  if (
    !!selectedNavItem &&
    (appNavItemTypeCheckers.isMappedEntityView(selectedNavItem) ||
      appNavItemTypeCheckers.isEntityApp(selectedNavItem))
  ) {
    return <EntityAppSideBar entityNavItem={selectedNavItem} />;
  }

  return <ProductAppSidebar />;
};

const Gate = () => {
  const { selectedProductApp } = useContext(V3SelectedAppContext);
  if (!selectedProductApp) {
    return null;
  }

  return (
    <Routes>
      <Route path={'/:itemKey/*'} element={<SidebarSwitch />} />
      <Route path={'*'} element={<ProductAppSidebar />} />
    </Routes>
  );
};

export default Gate;
